import type {
    ContentDefinition,
    PluginDefinition,
    UseBinding,
} from "@magic-md/core";
import { parseMagicMd } from "@magic-md/core";

const CONTENT_BODY_PARSE_CACHE_MAX_ENTRIES = 64;
const CONTENT_BODY_PARSE_CACHE_MAX_SOURCE_BYTES = 1_000_000;
const CONTENT_BODY_PARSE_CACHE_MAX_ENTRY_BYTES = 256_000;

export interface ContentInnerParse {
    definitions: Map<string, PluginDefinition>;
    contentDefinitions: Map<string, ContentDefinition>;
    uses: UseBinding[];
}

interface CachedContentInnerParse {
    sourceBytes: number;
    value: ContentInnerParse;
}

const contentBodyDefinitionCache = new Map<string, CachedContentInnerParse>();
let cachedSourceBytes = 0;

function sourceBytes(body: string): number {
    // JavaScript strings use UTF-16 storage in the common case. Counting two
    // bytes per code unit gives the cache a deterministic upper bound without
    // allocating another encoded copy of every document revision.
    return body.length * 2;
}

function removeCachedBody(body: string): void {
    const cached = contentBodyDefinitionCache.get(body);
    if (!cached) return;
    contentBodyDefinitionCache.delete(body);
    cachedSourceBytes = Math.max(0, cachedSourceBytes - cached.sourceBytes);
}

function trimContentBodyParseCache(): void {
    while (
        contentBodyDefinitionCache.size > CONTENT_BODY_PARSE_CACHE_MAX_ENTRIES ||
        cachedSourceBytes > CONTENT_BODY_PARSE_CACHE_MAX_SOURCE_BYTES
    ) {
        const oldestBody = contentBodyDefinitionCache.keys().next().value;
        if (typeof oldestBody !== "string") break;
        removeCachedBody(oldestBody);
    }
}

export function getCachedContentInnerParse(body: string): ContentInnerParse {
    const cached = contentBodyDefinitionCache.get(body);
    if (cached) {
        contentBodyDefinitionCache.delete(body);
        contentBodyDefinitionCache.set(body, cached);
        return cached.value;
    }

    const parsed = parseMagicMd(body);
    const value: ContentInnerParse = {
        definitions: new Map(parsed.definitions),
        contentDefinitions: new Map(parsed.contentDefinitions ?? new Map()),
        uses: [...(parsed.uses ?? [])],
    };
    const bytes = sourceBytes(body);

    // Large generated/template bodies are exactly the inputs that made the old
    // 200-entry caches retain hundreds of megabytes. They are cheaper to parse
    // again than to pin in memory across a long editor session.
    if (bytes > CONTENT_BODY_PARSE_CACHE_MAX_ENTRY_BYTES) return value;

    contentBodyDefinitionCache.set(body, { sourceBytes: bytes, value });
    cachedSourceBytes += bytes;
    trimContentBodyParseCache();
    return value;
}

export function resetContentInnerParseCacheForTests(): void {
    contentBodyDefinitionCache.clear();
    cachedSourceBytes = 0;
}

export function getContentInnerParseCacheStatsForTests(): {
    entries: number;
    sourceBytes: number;
} {
    return {
        entries: contentBodyDefinitionCache.size,
        sourceBytes: cachedSourceBytes,
    };
}
